import ApiRequest from "@/common/lib/apiRequest";
import BN from "bignumber.js";

const {
  JsonRpcProvider,
  Connection,
  SuiSystemStateUtil,
  TransactionBlock,
  normalizeSuiObjectId,
} = require("@mysten/sui.js");

const mutationTypes = {
  SET_AVAILABLE_BALANCE: "SET_AVAILABLE_BALANCE",
  SET_PRICES: "SET_PRICES",
  SET_STAKED_INFO: "SET_STAKED_INFO",
  SET_DELEGATIONS: "SET_DELEGATIONS",
  SET_NETWORK_INFO: "SET_NETWORK_INFO",
};

const state = {
  availableBalance: {
    fetching: false,
    result: null,
    error: null,
  },
  prices: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedInfo: {
    fetching: false,
    result: null,
    error: null,
  },
  delegations: {
    fetching: false,
    result: null,
    error: null,
  },
  networkInfo: {
    fetching: false,
    result: null,
    error: null,
  },
};

const actions = {
  async fetchRequiredInfo({ dispatch, rootGetters }) {
    return Promise.all([
      dispatch("fetchAvailableBalance"),
      dispatch("fetchDelegations"),
    ]);
  },
  fetchNetworkInfo({ dispatch }) {
    return dispatch("fetchSwuStakedInfo");
  },
  async fetchAvailableBalance({ rootGetters, commit }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_AVAILABLE_BALANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        `/noneth/sui/delegator/available_balance/${walletSelected.address}`
      );
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error, fetching: false });
    }
  },
  async fetchDelegations({ rootGetters, commit, state }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_DELEGATIONS, { fetching: true });
    try {
      const data = await ApiRequest.get(
        `/noneth/sui/delegator/delegations/${walletSelected.address}`
      );
      const result = data
        .map(item =>
          item.stakes.map(stakeItem => ({
            estimatedReward: stakeItem.estimatedReward,
            balance: stakeItem.principal,
            stakeActiveEpoch: stakeItem.stakeActiveEpoch,
            stakeRequestEpoch: stakeItem.stakeRequestEpoch,
            stakedSuiId: stakeItem.stakedSuiId,
            status: stakeItem.status,
            unstakeable: stakeItem.status === "Active",
            validator: item.validator,
          }))
        )
        .flat();
      commit(mutationTypes.SET_DELEGATIONS, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_DELEGATIONS, { error, fetching: false });
    }
  },
  async fetchAssetPriceChart({ commit }, { startTime, type = "h" } = {}) {
    commit(mutationTypes.SET_PRICES, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get(
        "/noneth/sui/token_prices/" + startTime,
        { params: { type } }
      );
      const result = getPriceChartData(data, type);
      commit(mutationTypes.SET_PRICES, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_PRICES, { error, fetching: false });
    }
  },
  async fetchSwuStakedInfo({ commit, getters }) {
    commit(mutationTypes.SET_STAKED_INFO, { fetching: true });
    try {
      const [result] = await Promise.all([
        ApiRequest.get("/noneth/sui/total_staked"),
      ]);
      commit(mutationTypes.SET_STAKED_INFO, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_INFO, { error, fetching: false });
    }
  },
  async getStakeRawTx({ rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const connection = new Connection({
      fullnode: process.env.VUE_APP_SUI_FULL_NODE,
    });
    const provider = new JsonRpcProvider(connection);
    const res = await provider.getCoins({
      owner: walletSelected.address,
      coinType: process.env.VUE_APP_SUI_COIN_TYPE,
    });
    const validatorAddress = process.env.VUE_APP_SUI_SWU_VALIDATOR_ADDRESS;
    const stakeAmount = new BN(payload.amount).multipliedBy(1e9).toNumber();
    const tx = await SuiSystemStateUtil.newRequestAddStakeTxn(
      provider,
      res.data.map(item => item.coinObjectId),
      stakeAmount,
      validatorAddress
    );
    return {
      transactionBlockSerialized: tx.serialize(),
      options: {
        showInput: true,
      },
    };
  },
  async getUnstakeRawTx({ rootGetters }, payload) {
    const SUI_SYSTEM_ADDRESS = "0x3";
    const SUI_SYSTEM_MODULE_NAME = "sui_system";
    const WITHDRAW_STAKE_FUN_NAME = "request_withdraw_stake";
    const SUI_SYSTEM_STATE_OBJECT_ID = normalizeSuiObjectId("0x5");
    const tx = new TransactionBlock();
    tx.moveCall({
      target: `${SUI_SYSTEM_ADDRESS}::${SUI_SYSTEM_MODULE_NAME}::${WITHDRAW_STAKE_FUN_NAME}`,
      arguments: [
        tx.object(SUI_SYSTEM_STATE_OBJECT_ID),
        tx.object(payload.stakedSuiId),
      ],
    });
    return {
      transactionBlockSerialized: tx.serialize(),
      options: {
        showInput: true,
      },
    };
  },
  async createTx({ rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    return ApiRequest.post(
      `/noneth/sui/transaction/create/${walletSelected.address}`,
      {
        type: payload.type,
        tx_hash: payload.txHash,
        metadata: payload.metadata,
      }
    );
  },
  resetState() {},
};

const mutations = {
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = {
      ...state.availableBalance,
      ...data,
    };
  },
  [mutationTypes.SET_PRICES](state, data) {
    state.prices = data;
  },
  [mutationTypes.SET_STAKED_INFO](state, data) {
    state.stakedInfo = {
      ...state.stakedInfo,
      ...data,
    };
  },
  [mutationTypes.SET_DELEGATIONS](state, data) {
    state.delegations = {
      ...state.delegations,
      ...data,
    };
  },
  [mutationTypes.SET_NETWORK_INFO](state, data) {
    state.networkInfo = {
      ...state.networkInfo,
      ...data,
    };
  },
};

const getters = {
  stakedInfo: state => state.stakedInfo,
  availableBalance: state => state.availableBalance,
  delegations: state => state.delegations,
  networkInfo: state => state.networkInfo,
  chainInfo: state => state.chainInfo,
};

const getPriceChartData = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  if (result.length === 1) return [result[0], result[0]];
  return result;
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
